// ⬇️ Import statements
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Select,
  Button,
  Table,
  Tag,
  Descriptions,
  Divider,
} from "antd";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller, useWatch } from "react-hook-form";
import { BookingData, BookingDetailsData } from "../../types/booking";
import { toast } from "react-hot-toast";
import {
  RefundRequestFormData,
  refundRequestSchema,
} from "../../lib/schemas/refundRequestSchema";
import { useStore } from "../../app/stores/store";
import { RefundStatus } from "../../lib/utils";
import dayjs from "dayjs";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import { PUBLIC_API_URL } from "../../environment";

type Props = {
  booking: BookingData | BookingDetailsData | null;
  onClose: () => void;
  open: boolean;
  onRefundSuccess?: () => void;
};

type RefundTableRow = {
  key: string;
  refundRequestId: string;
  refundRequestClientId: string | null;
  clientName: string;
  status: RefundStatus;
  requestedAmount: number;
  exitDate: string | null;
  remarks: string | null;
  createdAt: string;
  isClientLevel: boolean;
};


const AdminRefundRequestModal: React.FC<Props> = ({
  booking,
  onClose,
  open,
  onRefundSuccess,
}) => {
  if (!open || !booking) return null;
  const { bookingStore,userStore } = useStore();
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<RefundStatus | null>(
    null
  );

  const clients = booking.clients ?? [];
  const existingRequests = booking.refundRequests ?? [];

  // Get all client IDs that already have a refund request
  const requestedClientIds = new Set(
    existingRequests.flatMap(
      (r) =>
        r.refundRequestClients?.map((rc) => rc.client?.id).filter(Boolean) ?? []
    )
  );

  // Determine which clients still don't have a refund request
  const eligibleClients = clients.filter((c) => !requestedClientIds.has(c.id));

  const allClientsRequested =
    clients.length > 0 && eligibleClients.length === 0;
  const noClients = clients.length === 0;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<RefundRequestFormData>({
    resolver: zodResolver(refundRequestSchema),
    defaultValues: {
      bookingId: booking.id,
      exitDate: undefined,
      remarks: "",
      requestedAmount: undefined, // will be set programmatically (per-client)
      clientIds: [],
    },
  });

const downloadReceipt = async (refundRequestClientId: string) => {
  if (!refundRequestClientId) {
    alert("Refund Request Client ID not provided");
    return;
  }

  try {
    const response = await fetch(
      `${PUBLIC_API_URL}booking/refund/${refundRequestClientId}/receipt`,
      { method: "GET" }
    );

    if (!response.ok) throw new Error(`Failed to download receipt: ${response.statusText}`);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `receipt_${refundRequestClientId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Receipt download error:", error);
    alert("Failed to download receipt.");
  }
};


  const handleStatusUpdate = async () => {
    if (!selectedRequest || selectedStatus === null) return;
    try {
      await bookingStore.updateRefundRequestStatus(
        selectedRequest.id,
        selectedStatus
      );
      toast.success("Status updated successfully!");
      setShowStatusUpdate(false);
      setSelectedRequest(null);
      setSelectedStatus(null);
      onRefundSuccess?.();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const onSubmit = async (data: RefundRequestFormData) => {
    try {
      if (
        !data.exitDate ||
        !data.requestedAmount ||
        (clients.length > 0 && (!data.clientIds || data.clientIds.length === 0))
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      await bookingStore.createRefundRequest(data);

      // Force reload a moment after create
      setTimeout(async () => {
        try {
          if (booking?.id) await bookingStore.getBooking(booking.id);
        } catch (error) {
          console.error("Refresh error:", error);
        }
      }, 500);

      toast.success("Refund request submitted!");
      onRefundSuccess?.();
      reset();
      onClose();
    } catch (error: any) {
      console.error("Refund submission error:", error);
      toast.error(error.message || "Failed to submit refund request");
    }
  };

 const detailedRequestData: RefundTableRow[] =
  existingRequests.flatMap<RefundTableRow>((request) => {
    // Case 1: Per-client refund rows
    if (
      request.refundRequestClients &&
      request.refundRequestClients.length > 0
    ) {
      return request.refundRequestClients.map((rc): RefundTableRow => ({
        key: rc.id,
        refundRequestId: request.id,
        refundRequestClientId: rc.id,
        clientName: rc.client?.name ?? booking.customerName ?? "—",
        status: request.status,
        requestedAmount: request.requestedAmount,
        exitDate: request.exitDate ?? null,
        remarks: request.remarks ?? null,
        createdAt: request.createdAt,
        isClientLevel: true,
      }));
    }

    // Case 2: Legacy / single-client refunds
    return [
      {
        key: request.id,
        refundRequestId: request.id,
        refundRequestClientId: null,
        clientName: booking.customerName ?? "—",
        status: request.status,
        requestedAmount: request.requestedAmount,
        exitDate: request.exitDate ?? null,
        remarks: request.remarks ?? null,
        createdAt: request.createdAt,
        isClientLevel: false,
      },
    ];
  });


  const openStatusUpdateModal = (request: any) => {
    setSelectedRequest(request);
    setSelectedStatus(request.status);
    setShowStatusUpdate(true);
  };

  // ✅ Auto-calculation:
  // Per-client amount that each created request should store
  //const perClientAmount = Number(booking.securityDeposit ?? 0);

  const perClientAmount = (
  booking.includeSecurityDeposit && 
  Number(booking.customSecurityDeposit) > 0
)
  ? Number(booking.customSecurityDeposit)
  : Number(booking.securityDeposit ?? 0);

  
  // Watch the selected clients to compute total for display
  const clientIds = useWatch({ control, name: "clientIds" });
  const selectedCount = clientIds?.length ?? 0;
  const totalAmount = perClientAmount * selectedCount;

  // Important: store *per-client* amount in the form value that will be submitted
  useEffect(() => {
    setValue("requestedAmount", perClientAmount, { shouldValidate: true });
  }, [perClientAmount, setValue]);

  return (
    <Modal
      title={`Refund Requests - ${booking.serviceName}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      {/* Status Update Modal */}
      <Modal
        title="Update Refund Status"
        open={showStatusUpdate}
        onCancel={() => {
          setShowStatusUpdate(false);
          setSelectedRequest(null);
          setSelectedStatus(null);
        }}
        onOk={handleStatusUpdate}
        okText="Update Status"
      >
        <p className="mb-2">Current Status:</p>
        <Tag
          color={
            selectedRequest?.status === RefundStatus.Approved
              ? "green"
              : selectedRequest?.status === RefundStatus.Rejected
                ? "red"
                : "orange"
          }
        >
          {RefundStatus[selectedRequest?.status]}
        </Tag>

        <p className="mt-4 mb-2">Select New Status:</p>
        <Select
          style={{ width: "100%" }}
          value={selectedStatus ?? undefined}
          onChange={(val) => setSelectedStatus(val)}
        >
          <Select.Option value={RefundStatus.Approved}>
            <Tag color="green">Approved</Tag>
          </Select.Option>
          <Select.Option value={RefundStatus.Rejected}>
            <Tag color="red">Rejected</Tag>
          </Select.Option>
          <Select.Option value={RefundStatus.Pending}>
            <Tag color="orange">Pending</Tag>
          </Select.Option>
        </Select>
      </Modal>

      {/* Existing Requests Table */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Existing Refund Requests</h3>
        {detailedRequestData.length > 0 ? (
          <Table
            dataSource={detailedRequestData}
            pagination={false}
            expandable={{
              expandedRowRender: (request) => (
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Request ID">
                    {request.refundRequestClientId}
                  </Descriptions.Item>
                  <Descriptions.Item label="Exit Date">
                    {request.exitDate
                      ? dayjs(request.exitDate).format("YYYY-MM-DD HH:mm")
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Requested Amount">
                    {request.requestedAmount} AED
                  </Descriptions.Item>
                  <Descriptions.Item label="Remarks">
                    {request.remarks || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {dayjs(request.createdAt).format("YYYY-MM-DD HH:mm")}
                  </Descriptions.Item>
                </Descriptions>
              ),
            }}
          >
            <Table.Column title="Client" dataIndex="clientName" />
            <Table.Column
              title="Amount"
              dataIndex="requestedAmount"
              render={(amt) => `${amt} AED`}
            />
            <Table.Column
              title="Status"
              dataIndex="status"
              render={(status: RefundStatus) => (
                <Tag
                  color={
                    status === RefundStatus.Approved
                      ? "green"
                      : status === RefundStatus.Rejected
                        ? "red"
                        : "orange"
                  }
                >
                  {RefundStatus[status]}
                </Tag>
              )}
            />

            <Table.Column
              title="Actions"
              render={(_, record: any) => (
                <div className="flex gap-2">
                 
                       {(userStore.isCashier() || userStore.isAdmin()||userStore.isAirportCashier() ) &&

                  <Button
                    icon={<EditOutlined />}
                    onClick={() => openStatusUpdateModal(record)}
                  >
                    Update Status
                  </Button>
              }

                  {/* {record.status === RefundStatus.Approved && ( */}
       {record.refundRequestClientId && (
  <Button
    type="primary"
    icon={<DownloadOutlined />}
    onClick={() =>
      downloadReceipt(record.refundRequestClientId)
    }
  >
    Receipt
  </Button>
)}

                  {/* )} */}

                </div>
              )}
            />
          </Table>
        ) : (
          <p className="text-gray-500">No refund requests found.</p>
        )}
      </div>

      {/* Warnings */}
      {noClients && (
        <div className="p-3 bg-yellow-50 border border-yellow-300 rounded mb-4 text-yellow-800">
          Refund requests cannot be created because there are no clients in this
          booking.
        </div>
      )}
      {allClientsRequested && !noClients && (
        <div className="p-3 bg-red-50 border border-red-200 rounded mb-4 text-red-700">
          All clients in this booking already have refund requests.
        </div>
      )}

      {/* Create Refund Form */}
      {!noClients && !allClientsRequested && (
        <>
          <Divider />
          <h3 className="text-lg font-semibold mb-4">
            Create New Refund Request
          </h3>
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            {/* Hidden bookingId */}
            <Controller
              name="bookingId"
              control={control}
              render={({ field }) => <input type="hidden" {...field} />}
            />

            {/* Hidden requestedAmount (per-client) to submit */}
            <Controller
              name="requestedAmount"
              control={control}
              render={({ field }) => (
                <input type="hidden" {...field} value={perClientAmount} />
              )}
            />

            <Form.Item
              label="Exit Date"
              validateStatus={errors.exitDate ? "error" : ""}
              help={errors.exitDate?.message}
            >
              <Controller
                name="exitDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    style={{ width: "100%" }}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : null)
                    }
                  />
                )}
              />
            </Form.Item>

            {/* Display-only total amount */}
            <Form.Item
              label="Total Refund Amount (AED)"
              // you can surface requestedAmount errors here if needed
              help={
                errors.requestedAmount?.message ??
                (perClientAmount > 0 && selectedCount > 0
                  ? `Calculated: ${selectedCount} × ${perClientAmount} = ${totalAmount} AED`
                  : "Select clients to calculate total.")
              }
            >
              <InputNumber
                value={totalAmount}
                disabled
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              label="Remarks"
              validateStatus={errors.remarks ? "error" : ""}
              help={errors.remarks?.message}
            >
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    rows={3}
                    placeholder="Additional notes"
                  />
                )}
              />
            </Form.Item>

            <Form.Item
              label="Clients"
              validateStatus={errors.clientIds ? "error" : ""}
              help={errors.clientIds?.message}
            >
              <Controller
                name="clientIds"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    mode="multiple"
                    placeholder="Select clients"
                    options={eligibleClients.map((client) => ({
                      label: client.name,
                      value: client.id,
                    }))}
                    onChange={(vals) => field.onChange(vals)}
                  />
                )}
              />
            </Form.Item>

            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Submit Request
              </Button>
            </div>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default AdminRefundRequestModal;
